import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Infinity as InfinityIcon, Timer, Sparkles, ShieldCheck, BarChart3,
  Trophy, Brain, Cpu, BookOpenCheck, LineChart, Atom, FlaskConical, Sigma, Check, Star, PlayCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MathText } from "@/components/MathText";
import { Footer } from "@/components/Footer";
import { cn } from "@/lib/utils";

const HERO = "https://images.unsplash.com/photo-1612534604596-e75ea98f1e29?auto=format&fit=crop&w=1600&q=80";

const reveal = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={cn("mx-auto max-w-7xl px-4 sm:px-6", className)}>{children}</section>
);

const Eyebrow = ({ children }) => (
  <span className="text-xs font-medium uppercase tracking-[0.25em] text-primary">{children}</span>
);

/* ---------------- Data ---------------- */
const STATS = [
  { n: "50,000+", l: "Questions" },
  { n: "10,000+", l: "Students" },
  { n: "95%", l: "Accuracy" },
  { n: "500+", l: "IIT Selections" },
];

const FEATURES = [
  { icon: Sparkles, t: "AI-Generated Questions", d: "500+ new questions generated daily using advanced AI, covering all JEE topics with varied difficulty." },
  { icon: ShieldCheck, t: "Expert Verified", d: "Every AI question is reviewed by IIT graduates to ensure accuracy and JEE relevance." },
  { icon: Brain, t: "Adaptive Learning", d: "Our AI analyses your performance and creates personalised sessions targeting your weak areas." },
  { icon: Timer, t: "Timed Practice", d: "Simulate real exam conditions with timed tests and track your speed improvement." },
  { icon: BarChart3, t: "Detailed Analytics", d: "Track progress with comprehensive stats, accuracy trends, and topic-wise analysis." },
  { icon: Trophy, t: "Leaderboards & Rewards", d: "Compete with peers, earn XP, maintain streaks, and unlock badges as you progress." },
];

const STEPS = [
  { icon: Cpu, t: "AI Generates", d: "Every night, our AI creates 500+ new questions based on JEE syllabus and patterns." },
  { icon: ShieldCheck, t: "Experts Verify", d: "IIT graduates review each question for accuracy, difficulty, and relevance." },
  { icon: BookOpenCheck, t: "You Practice", d: "Access fresh, verified questions daily with detailed solutions and explanations." },
  { icon: LineChart, t: "Track Progress", d: "Our AI analyses your performance and provides personalised recommendations." },
];

const SUBJECTS = [
  { icon: Atom, t: "Physics", count: "15,000+ questions", chapters: ["Mechanics", "Thermodynamics", "Electromagnetism", "Optics", "Modern Physics"], color: "text-blue-500" },
  { icon: FlaskConical, t: "Chemistry", count: "18,000+ questions", chapters: ["Physical Chemistry", "Organic Chemistry", "Inorganic Chemistry"], color: "text-emerald-500" },
  { icon: Sigma, t: "Mathematics", count: "17,000+ questions", chapters: ["Algebra", "Calculus", "Coordinate Geometry", "Trigonometry", "Vectors & 3D"], color: "text-violet-500" },
];

const PLANS = [
  { name: "Free", price: "₹0", per: "forever", tag: "Perfect for getting started", cta: "Get Started", to: "/register", popular: false,
    features: ["20 questions per day", "2 mock tests per month", "Basic analytics", "AI doubt resolution", "Community access"] },
  { name: "Pro", price: "₹499", per: "per month", tag: "Best for serious aspirants", cta: "Start Pro Trial", to: "/register", popular: true,
    features: ["Unlimited questions", "Unlimited mock tests", "Advanced analytics", "Priority doubt resolution", "Previous year papers", "Performance predictions", "Expert consultations"] },
  { name: "Premium", price: "₹999", per: "per month", tag: "Complete preparation suite", cta: "Go Premium", to: "/register", popular: false,
    features: ["Everything in Pro", "1-on-1 mentorship", "Custom study plan", "Video solutions", "Offline access", "Priority support"] },
];

const TESTIMONIALS = [
  { q: "The adaptive practice mode identified my weak areas perfectly. The AI-generated questions were surprisingly challenging and JEE-relevant.", i: "AS", n: "Arjun Sharma", r: "AIR 156", e: "JEE Advanced 2024" },
  { q: "I practiced 100+ questions daily on Z2Infinity. The detailed solutions helped me understand concepts deeply. Highly recommended!", i: "PP", n: "Priya Patel", r: "AIR 89", e: "JEE Advanced 2024" },
  { q: "The gamification kept me motivated throughout my preparation. Streaks and leaderboards made practice fun!", i: "RV", n: "Rahul Verma", r: "99.8 %ile", e: "JEE Main 2024" },
];

/* ---------------- Page ---------------- */
export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Student studying at night" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-950/95 via-zinc-950/85 to-zinc-900/70" />
        </div>
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 sm:px-6 lg:grid-cols-2 lg:py-32">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium tracking-wide text-white/80 backdrop-blur">
              <Star className="h-3.5 w-3.5 fill-primary text-primary" /> Trusted by 10,000+ JEE Aspirants
            </span>
            <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-6xl">
              Crack IIT-JEE with
              <br />
              <span className="text-primary">AI-Powered</span> Practice
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
              500+ new questions generated daily by AI, verified by IIT experts. Adaptive learning
              that evolves with you — across JEE &amp; NEET.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" data-testid="hero-start-btn" className="rounded-full px-7 text-base">
                <Link to="/register">Start Free Practice <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-demo-btn" className="rounded-full border-white/25 bg-white/5 px-7 text-base text-white hover:bg-white/10 hover:text-white">
                <a href="#how-it-works"><PlayCircle className="mr-1.5 h-4 w-4" /> Watch Demo</a>
              </Button>
            </div>
            <div className="mt-14 grid grid-cols-4 gap-6">
              {STATS.map((s) => (
                <div key={s.l}>
                  <div className="font-heading text-2xl font-bold text-white sm:text-3xl">{s.n}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 sm:text-xs">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Dashboard/question preview */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.15 }} className="hidden lg:block">
            <div className="rounded-3xl border border-white/10 bg-zinc-900/70 p-2 shadow-2xl backdrop-blur">
              <div className="flex items-center gap-1.5 px-3 py-2">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-green-400/70" />
                <span className="ml-3 text-xs text-white/40">dashboard.jeemaster.com</span>
              </div>
              <div className="rounded-2xl bg-zinc-950 p-5">
                <div className="mb-3 flex items-center gap-2">
                  <Badge className="rounded-full bg-primary/15 text-primary">JEE Main · Physics</Badge>
                  <Badge variant="outline" className="rounded-full border-white/15 text-white/60">Medium</Badge>
                </div>
                <MathText block className="text-[15px] leading-relaxed text-white/90" text={"A projectile is launched at $45^\\circ$ with speed $u$. Find $\\dfrac{H}{R}$, the ratio of maximum height to range."} />
                <div className="mt-4 space-y-2">
                  {["$1:2$", "$1:4$", "$1:1$", "$2:1$"].map((o, i) => (
                    <div key={i} className={cn("flex items-center gap-3 rounded-lg border p-2.5 text-sm", i === 1 ? "border-green-500 bg-green-500/10 text-white" : "border-white/10 text-white/70")}>
                      <span className="flex h-6 w-6 items-center justify-center rounded bg-white/10 text-xs font-bold">{["A", "B", "C", "D"][i]}</span>
                      <MathText text={o} />
                      {i === 1 && <Check className="ml-auto h-4 w-4 text-green-400" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <Section className="py-20 sm:py-24">
        <motion.div {...reveal} transition={{ duration: 0.4 }} className="mb-14 max-w-2xl">
          <Eyebrow>Features</Eyebrow>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Everything You Need to Crack JEE</h2>
          <p className="mt-3 text-muted-foreground">A personalised, AI-powered platform with expert-verified content and real-time analytics to maximise your preparation.</p>
        </motion.div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div key={f.t} {...reveal} transition={{ duration: 0.4, delay: i * 0.06 }} className="rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/40">
              <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary"><f.icon className="h-5 w-5" /></span>
              <h3 className="font-heading text-lg font-semibold">{f.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <div id="how-it-works" className="border-y border-border bg-secondary/30">
        <Section className="py-20 sm:py-24">
          <motion.div {...reveal} transition={{ duration: 0.4 }} className="mb-14 max-w-2xl">
            <Eyebrow>How it works</Eyebrow>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">How Z2Infinity Works</h2>
            <p className="mt-3 text-muted-foreground">Our VIGS (Verified Intelligent Generation System) ensures you always get high-quality, expert-approved practice material.</p>
          </motion.div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <motion.div key={s.t} {...reveal} transition={{ duration: 0.4, delay: i * 0.08 }} className="relative rounded-2xl border border-border bg-card p-6">
                <span className="font-heading text-4xl font-black text-primary/15">0{i + 1}</span>
                <span className="mt-2 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><s.icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-heading text-lg font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* SYLLABUS */}
      <Section className="py-20 sm:py-24">
        <motion.div {...reveal} transition={{ duration: 0.4 }} className="mb-14 max-w-2xl">
          <Eyebrow>Coverage</Eyebrow>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Complete JEE Syllabus Coverage</h2>
          <p className="mt-3 text-muted-foreground">Practice questions from all three subjects with chapter-wise and topic-wise organisation.</p>
        </motion.div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {SUBJECTS.map((s, i) => (
            <motion.div key={s.t} {...reveal} transition={{ duration: 0.4, delay: i * 0.08 }} className="rounded-2xl border border-border bg-card p-6">
              <div className="flex items-center gap-3">
                <span className={cn("flex h-11 w-11 items-center justify-center rounded-xl bg-secondary", s.color)}><s.icon className="h-5 w-5" /></span>
                <div>
                  <h3 className="font-heading text-lg font-semibold">{s.t}</h3>
                  <p className="text-xs text-muted-foreground">{s.count}</p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {s.chapters.map((c) => (
                  <span key={c} className="rounded-full border border-border bg-secondary/50 px-3 py-1 text-xs text-foreground/70">{c}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* PRICING */}
      <div className="border-y border-border bg-secondary/30">
        <Section className="py-20 sm:py-24">
          <motion.div {...reveal} transition={{ duration: 0.4 }} className="mb-14 text-center">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Simple, Transparent Pricing</h2>
            <p className="mx-auto mt-3 max-w-md text-muted-foreground">Start free and upgrade when you need more. No hidden fees.</p>
          </motion.div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {PLANS.map((p, i) => (
              <motion.div key={p.name} {...reveal} transition={{ duration: 0.4, delay: i * 0.08 }}
                className={cn("relative flex flex-col rounded-2xl border bg-card p-7", p.popular ? "border-primary shadow-[0_8px_40px_-12px_hsl(9_86%_58%_/_0.3)]" : "border-border")}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">Most Popular</span>}
                <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-heading text-4xl font-bold">{p.price}</span>
                  <span className="text-sm text-muted-foreground">/{p.per}</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{p.tag}</p>
                <ul className="mt-6 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> <span className="text-foreground/80">{f}</span>
                    </li>
                  ))}
                </ul>
                <Button asChild variant={p.popular ? "default" : "outline"} data-testid={`plan-${p.name.toLowerCase()}-btn`} className="mt-7 w-full rounded-xl py-6">
                  <Link to={p.to}>{p.cta}</Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* TESTIMONIALS */}
      <Section className="py-20 sm:py-24">
        <motion.div {...reveal} transition={{ duration: 0.4 }} className="mb-14 max-w-2xl">
          <Eyebrow>Testimonials</Eyebrow>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Loved by JEE Toppers</h2>
          <p className="mt-3 text-muted-foreground">Hear from students who cracked JEE with Z2Infinity.</p>
        </motion.div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <motion.div key={t.n} {...reveal} transition={{ duration: 0.4, delay: i * 0.08 }} className="flex flex-col rounded-2xl border border-border bg-card p-6">
              <div className="mb-3 flex gap-0.5">
                {[...Array(5)].map((_, k) => <Star key={k} className="h-4 w-4 fill-primary text-primary" />)}
              </div>
              <p className="flex-1 text-sm leading-relaxed text-foreground/80">"{t.q}"</p>
              <div className="mt-5 flex items-center gap-3 border-t border-border pt-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">{t.i}</span>
                <div>
                  <div className="text-sm font-semibold">{t.n}</div>
                  <div className="text-xs text-muted-foreground">{t.r} · {t.e}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="pb-24">
        <motion.div {...reveal} transition={{ duration: 0.4 }} className="relative overflow-hidden rounded-3xl border border-border bg-zinc-950 px-6 py-16 text-center">
          <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(600px circle at 50% 0%, hsl(9 86% 58% / 0.25), transparent 60%)" }} />
          <div className="relative">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">Start Your JEE Journey Today</h2>
            <p className="mx-auto mt-3 max-w-md text-white/60">Join 10,000+ students who are preparing smarter with AI.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" data-testid="cta-register-btn" className="rounded-full px-7 text-base">
                <Link to="/register">Create Free Account <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="cta-login-btn" className="rounded-full border-white/25 bg-white/5 px-7 text-base text-white hover:bg-white/10 hover:text-white">
                <Link to="/login">Sign In</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </Section>

      <Footer />
    </div>
  );
}
